import React, { useState, useEffect, useCallback } from 'react';
import {
  Film, Plus, Search, Trash2, Video,
} from 'lucide-react';
import { LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GymContentTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [gymList, setGymList] = useState([]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/gym-content`);
      if (resp.ok) setEntries(await resp.json());
    } catch (e) { console.error('Failed to load gym content:', e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchEntries();
    // Fetch gym list for dropdown
    import('../../lib/hyroxGymData').then(m => {
      const gyms = m.ALL_GYMS.map(g => ({ key: `${g.name}|${g.city}|${g.state}`, label: `${g.name} — ${g.city}, ${g.state}` }));
      setGymList(gyms.sort((a, b) => a.label.localeCompare(b.label)));
    }).catch(() => {});
  }, [fetchEntries]);

  const handleDelete = async (gymKey) => {
    if (!window.confirm('Remove content from this gym?')) return;
    const token = sessionStorage.getItem('admin_token');
    await fetch(`${API_URL}/api/admin/gym-content/${encodeURIComponent(gymKey)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchEntries();
  };

  const handleEdit = (entry) => {
    setEditing(entry);
    setShowForm(true);
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditing(null);
    fetchEntries();
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6" data-testid="admin-gym-content-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Gym Content Portfolio</h3>
          <p className="text-sm text-gray-500">{entries.length} gyms with content</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors"
          data-testid="add-gym-content-btn">
          <Plus className="w-4 h-4" /> Add Content
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <GymContentForm
          gymList={gymList}
          existingKeys={entries.map(e => e.gym_key)}
          editing={editing}
          onSaved={handleSaved}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {/* Entries List */}
      {entries.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No gym content yet. Click "Add Content" to associate videos with gyms.</div>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => {
            const parts = entry.gym_key.split('|');
            const gymName = parts[0] || 'Unknown';
            const location = parts.length >= 3 ? `${parts[1]}, ${parts[2]}` : '';
            return (
              <div key={entry.gym_key} className="bg-white rounded-xl border border-gray-100 p-5" data-testid={`gym-entry-${entry.gym_key}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">{gymName}</span>
                      <span className="text-xs text-gray-400">{location}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-600 font-medium">{entry.videos?.length || 0} videos</span>
                    </div>
                    {entry.description && <p className="text-xs text-gray-500 mb-2">{entry.description}</p>}
                    {entry.videos && entry.videos.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {entry.videos.map((v, i) => (
                          <div key={v.title} className="flex items-center gap-1.5 text-xs bg-gray-50 rounded px-2 py-1">
                            <Video className="w-3 h-3 text-gray-400" />
                            <span className="text-gray-600">{v.title}</span>
                            <span className="text-[10px] text-gray-400">({v.type})</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {entry.updated_at && <p className="text-[10px] text-gray-400 mt-2">Updated: {new Date(entry.updated_at).toLocaleDateString()}</p>}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => handleEdit(entry)}
                      className="px-2.5 py-1.5 text-[10px] bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                      data-testid={`edit-${entry.gym_key}`}>
                      Edit
                    </button>
                    <button onClick={() => handleDelete(entry.gym_key)}
                      className="px-2.5 py-1.5 text-[10px] bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                      data-testid={`delete-${entry.gym_key}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GymContentForm({ gymList, existingKeys, editing, onSaved, onCancel }) {
  const [gymKey, setGymKey] = useState(editing?.gym_key || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [videos, setVideos] = useState(editing?.videos || [{ url: '', title: '', type: 'Race Recap', poster: '' }]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const filteredGyms = gymList.filter(g =>
    !existingKeys.includes(g.key) || g.key === editing?.gym_key
  ).filter(g => !search || g.label.toLowerCase().includes(search.toLowerCase()));

  const addVideo = () => setVideos(prev => [...prev, { url: '', title: '', type: 'Race Recap', poster: '' }]);
  const removeVideo = (i) => setVideos(prev => prev.filter((_, idx) => idx !== i));
  const updateVideo = (i, field, value) => setVideos(prev => prev.map((v, idx) => idx === i ? { ...v, [field]: value } : v));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gymKey || videos.every(v => !v.url)) return;
    setSaving(true);
    const token = sessionStorage.getItem('admin_token');
    const validVideos = videos.filter(v => v.url.trim());
    await fetch(`${API_URL}/api/admin/gym-content`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ gym_key: gymKey, videos: validVideos, description: description || null }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-teal-200 p-6 space-y-4" data-testid="gym-content-form">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-gray-900">{editing ? 'Edit Gym Content' : 'Add Content to Gym'}</h4>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
      </div>

      {/* Gym Selector */}
      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Select Gym *</label>
        <input
          type="text"
          placeholder="Search gyms..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2 focus:outline-none focus:border-teal-400"
          data-testid="gym-search-input"
        />
        {!gymKey && (
          <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-lg">
            {filteredGyms.slice(0, 20).map(g => (
              <button key={g.key} type="button" onClick={() => { setGymKey(g.key); setSearch(''); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-teal-50 transition-colors border-b border-gray-50 last:border-0"
                data-testid={`gym-option-${g.key}`}>
                {g.label}
              </button>
            ))}
            {filteredGyms.length > 20 && <p className="px-3 py-2 text-xs text-gray-400">Showing 20 of {filteredGyms.length} — refine search</p>}
          </div>
        )}
        {gymKey && (
          <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 border border-teal-200 rounded-lg">
            <span className="text-sm text-teal-700 flex-1">{gymList.find(g => g.key === gymKey)?.label || gymKey}</span>
            {!editing && <button type="button" onClick={() => setGymKey('')} className="text-teal-500 text-xs hover:text-teal-700">Change</button>}
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Description</label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)}
          placeholder="e.g. Filmed by NinjAthlete x Brandon Davie"
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
          data-testid="gym-content-description" />
      </div>

      {/* Videos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Videos</label>
          <button type="button" onClick={addVideo}
            className="text-xs text-teal-500 hover:text-teal-700 flex items-center gap-1" data-testid="add-video-btn">
            <Plus className="w-3 h-3" /> Add Video
          </button>
        </div>
        <div className="space-y-3">
          {videos.map((v, i) => (
            <div key={v.title} className="bg-gray-50 rounded-lg p-3 space-y-2" data-testid={`video-entry-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium text-gray-500 uppercase">Video {i + 1}</span>
                {videos.length > 1 && (
                  <button type="button" onClick={() => removeVideo(i)} className="text-red-400 hover:text-red-600" data-testid={`remove-video-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <input type="url" placeholder="Video URL *" value={v.url} onChange={e => updateVideo(i, 'url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-teal-400" data-testid={`video-url-${i}`} />
              <div className="grid grid-cols-3 gap-2">
                <input type="text" placeholder="Title *" value={v.title} onChange={e => updateVideo(i, 'title', e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-teal-400" data-testid={`video-title-${i}`} />
                <select value={v.type} onChange={e => updateVideo(i, 'type', e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-teal-400" data-testid={`video-type-${i}`}>
                  {['Race Recap', 'Hype Reel', 'Event Film', 'Athlete Profile', 'Gym Content', 'Showreel'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input type="text" placeholder="Poster URL (optional)" value={v.poster} onChange={e => updateVideo(i, 'poster', e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-teal-400" data-testid={`video-poster-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={saving || !gymKey}
          className="px-6 py-2.5 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="save-gym-content-btn">
          {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
          {saving ? 'Saving...' : editing ? 'Update Content' : 'Save Content'}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
    </form>
  );
}
